import React, {useContext} from 'react';

import Yup from 'yup';
import {Input, ComboBox, RadioButton} from 'views/FormElements';

import AppConstants from 'AppConstants';
import AppUtil from 'AppUtil';

import 'react-select/dist/react-select.css';

import OrderForm from 'views/order/OrderForm';

import ImgUploadForm from 'views/ImgUploadForm';
import ProductOrderForm from 'views/order/ProductOrderForm';
import {OrderContext} from 'views/order/OrderForm';

import ProductUtil from 'util/ProductUtil';

const MUSIC_PLAYER_BOTTOM_POS = "Bottom Position";
const MUSIC_PLAYER_TOP_POS = "Top Position";

const styles = {
	extraCostInfo: {color: '#2e292b', marginTop: '10px', background: '#dfdfdf', padding: '5px 10px', borderBottom: "2px solid #f8931c", fontWeight: 'bold'},
}

const MusicPlaqueVariant = ({productVariant, selectedVariant}) => {
	const variantKey = ProductUtil.getProductVariantKey(productVariant);
	
	let selectedVariantDetail = null;
	const radioButtons = productVariant.variants.filter(v => v.visible === true).map(v => {
		const isChecked = selectedVariant === v.variantId;

		if(isChecked)
			selectedVariantDetail = v;
		return <RadioButton style={{flexGrow: '1'}} name={variantKey} 
						value={v.variantId} 
						label={<span style={{color: '#596161'}}>{v.label}</span>} 
						checked={isChecked} 
						key={v.variantId} />
	});

	const priceAddition = selectedVariantDetail? (selectedVariantDetail.priceAddition || 0): 0;
	return <React.Fragment>
		<label>{productVariant.variantLabel}</label>

		<div className='flexRow'>
			{radioButtons}
		</div>

		{selectedVariantDetail && priceAddition > 0 &&
			<div style={styles.extraCostInfo}><span style={{color: '#f49600'}}>Extra Rs. {AppUtil.formatNumber(priceAddition)}</span> for '{selectedVariantDetail.label}'</div>
		}
	</React.Fragment>
}

const MusicPlaqueInput = ({isAcryclicPlaque}) => {
	const {values, product, setFieldValue} = useContext(OrderContext);

	const musicPlayerOptions = [MUSIC_PLAYER_BOTTOM_POS, MUSIC_PLAYER_TOP_POS].map(p => {
		return {label: p, value: p}
	});
	
	const musicPlaqueVariants = !product.productVariants? null: product.productVariants.filter(pv => pv.visible === true).map(pv => {
		const variantKey = ProductUtil.getProductVariantKey(pv);
		return <MusicPlaqueVariant productVariant={pv} selectedVariant={values[variantKey]} key={"MusicPlaqueVariant_"+pv.variantLabel}/>
	});

	return <React.Fragment>
		<div>
			<Input label="Song Title: " name="songTitle" type="text" />

			<Input label={<span>Artist (Optional): </span>} name="artist" type="text" placeholder="" />

			<Input label={<span>QR Code Link (Optional): </span>} name="qrCodeLink" type="text" placeholder="Link to open upon scanning a QR placed on the back of the plaque" />
			{
				isAcryclicPlaque === true &&
				<React.Fragment>
					<small>(If provided, a QR code will be placed on the back of the music plaque)</small>
					<br />
				</React.Fragment>
			}
			

			{musicPlaqueVariants}
			<br />

			<label>Image/Artwork:</label>
			<div className='flexRow'>
				
				<RadioButton style={{flexGrow: '1'}} name="imgPreference" value={"customUpload"} 
						label={<span style={{color: '#596161'}}>I want to upload my Image</span>} 
						checked={values.imgPreference === "customUpload"} />

				<RadioButton style={{flexGrow: '1'}} name="imgPreference" value={"helpFindImg"} 
							label={<span style={{color: '#596161'}}>Find me something from the Internet</span>} 
							checked={values.imgPreference === "helpFindImg"} 
							onClick={() => {
								setFieldValue('customerUploadedImage', null);
							}}/>
			</div>

			{
				values.imgPreference === "customUpload" &&
				<ImgUploadForm previewId="customerImageFile" className="cutomOrderImageForm"  
					imgWidth="100px" imgHeight="100px"
					onImgChange={(uploadedImgFile) => {
						setFieldValue('customerUploadedImage', uploadedImgFile);
					}} 

					onImgReset={() => {
						setFieldValue('customerUploadedImage', null);
					}}
					/>
			}

			{
				values.imgPreference === "helpFindImg" &&
				<span style={{color: 'white', background: 'gray', padding: '2px 10px'}}>We will email you or DM you the image for your approval before printing.</span>
			}

			<Input name="customerImage" type="hidden" />
		</div>

		{
		isAcryclicPlaque === false &&
		<ComboBox label="Music Player Placement: " name="musicPlayerPlacement" value={values.musicPlayerPlacement} 
					options={musicPlayerOptions} placeholder="Select Music Player Placement" />
		}
	</React.Fragment>
};


class MusicPlaqueForm extends ProductOrderForm {

	constructor(props) {
		super(props);

		this.isAcryclicPlaque = this.isAcryclicPlaque.bind(this);
	}

	initialValues() {
		let order = super.initialValues();
		order.songTitle = '';
		order.artist = '';
		order.qrCodeLink = '';
		
		order.imgPreference = '';
		order.musicPlayerPlacement = MUSIC_PLAYER_BOTTOM_POS;
		order.productSize = 'A4';
		order.quantity = this.props.quantity || 1;
		order.additionalNote = '';
		order.paymentPreference = '';

		order.customerUploadedImage = null;
		
		return order;
	}

	validationScheme() {
		const validation = super.validationScheme();

		validation.songTitle = Yup.string().required("Enter Song Title");
		validation.imgPreference = Yup.string().required("Select an image preference");

		validation.customerUploadedImage = Yup.string().when('imgPreference', (imgPreference, schema) => {
								if(imgPreference === 'customUpload')
									return Yup.mixed().required("Please Upload an image");

								return Yup.mixed().nullable();
		});
						
		validation.musicPlayerPlacement = Yup.string().required("Select Music Player placement in Music Player.");
		return validation;
	}

	createOrderRequest(formValue) {
		let order = super.createOrderRequest(formValue);
		
		order.orderMetaData = order.orderMetaData || {};
		order.orderMetaData['Song Title'] = formValue.songTitle;

		if(formValue.artist) order.orderMetaData["Artist"] = formValue.artist;
		if(formValue.qrCodeLink) order.orderMetaData["Link"] = formValue.qrCodeLink;
		if(formValue.imgPreference === 'helpFindImg') order.orderMetaData['Img Preference'] = "Something from the Internet";
		if(formValue.imgPreference === 'customUpload') order.orderMetaData['Img Preference'] = "User Uploaded Image";

		if(!this.isAcryclicPlaque())
			order.orderMetaData['Music Player Placement'] = formValue.musicPlayerPlacement;
		
		return order;
	}

	displayDeliveryInputs(formValue) {
        const quantity = formValue.quantity;
        const productSize = formValue.productSize;

		const imgPreference = formValue.imgPreference;

        return (!isNaN(quantity) && productSize !== '' && formValue.songTitle && imgPreference)
		&& (
			(imgPreference === 'customUpload' && formValue.customerUploadedImage !== null) || (imgPreference === 'helpFindImg')
		);
    }

	isAcryclicPlaque() {
		return AppConstants.ACRYCLIC_MUSIC_PLAQUE === this.props.product.id;
	}


	isPrePaymentRequired(formValue, product) {
		return true;
	}
	
	render() {
		const initialValues = this.initialValues();
        const validationScheme = this.validationScheme();

        return <OrderForm initialValues={initialValues} validationScheme={validationScheme}
                product={this.props.product} 
                createOrderRequest={this.createOrderRequest}
                displayDeliveryInputs={this.displayDeliveryInputs}  
				isPrePaymentRequired={this.isPrePaymentRequired} 
                render={() => {
                    return <React.Fragment>
                            <MusicPlaqueInput isAcryclicPlaque={this.isAcryclicPlaque()} />
                    </React.Fragment>
            }
        }/>
	}
}


export default MusicPlaqueForm;

	


// WEBPACK FOOTER //
// ./src/views/order/MusicPlaqueForm.js